
import React from 'react'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { composeWidget, createWidgetInitFunction } from 'widgets-shared/core'
import { Poller } from 'widgets-shared/services'
import Carousel from 'smartstream-widgets/controls/Carousel/Carousel'
import { withHotspotTracking } from 'smartstream-widgets/enhancers/withHotspotTracking'
import { getOnSlideRenderHandler } from 'smartstream-widgets/utils/widgetDataChanged'
import { withMuiThemeProvider } from 'smartstream-widgets/enhancers/withMuiThemeProvider'
import MainLinesComponent from './MainLinesComponent'
import BookmakerFooter from 'smartstream-widgets/components/BookmakerFooter/BookmakerFooter'
import { fallbackChecker } from 'smartstream-widgets/utils/fallbackChecker'

import { WidgetInitializationParameters } from 'widgets-shared/core'
import { ConfigOptions, LoggerFunction, TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { Props, State, MainLinesData, MainLinesFixtureData } from './types'
import { isPostMatchStatus } from 'smartstream-widgets/utils/isPostMatchStatus'
import PostMatchComponent from '../../components/PostMatchComponent/PostMatchComponent'

const styles = (theme: Theme) => createStyles({
  bookmakerContainer: {
    minHeight: 18,
    padding: `0px 20px`
  },
  bookmakerLogo: {
    margin: `4px 2px`,
    height: 13
  },
  bookmakerFooter: {
    padding: 0
  },
  carouselButton: {
    flex: 'none',
    position: 'absolute',
    top: 44,
    '&:disabled': {
      opacity: 0.5
    }
  },
  carouselButtonLeft: {
    padding: `9px 15px 9px 3px`
  },
  carouselButtonRight: {
    padding: `9px 3px 9px 15px`,
    right: 0
  },
  controlCaptions: {
    fontSize: 9
  },
  controlIconsSvg: {
    fontSize: 12
  },
  ctaLabel: {
    fontSize: 10
  },
  oddsByBookmaker: {
    fontSize: 10
  },
  navigationElements: {
    background: 'transparent',
    padding: 0,
    position: 'absolute',
    top: 0,
    width: '100%'
  },
  container: {
    boxSizing: 'border-box',
    height: '100%',
    position: 'relative',
    backgroundColor: theme.palette.background.default
  },
  singleContainer: {
    height: '100%'
  },
  disclaimerText:{
    fontSize: 8
  }
})

export class MainLines extends React.Component<Props, State> {
  poller: Poller
  containerRef: React.RefObject<HTMLDivElement>

  constructor(props: Props) {
    super(props)
    const {
      config,
      data: {
        fixtures,
        disclaimer,
        bookmakers,
        footerLink
      } = {},
      routingKey,
      renderParameters: {
        group,
        productName,
        region,
        country,
        fixtureSource,
        themeMode,
        postMatchTimeFrame
      } = {}
    } = props
    const {
      monetization: {
        defaultAdStrategy: {
          height: advertHeight,
          type: advertType
        } = {}
      } = {}
    } = config
    this.containerRef = React.createRef()

    const pollerOptions = {
      url: config['POLL_URL'],
      frequency: config['POLLING_FREQUENCY'],
      queryParams: (productName && { group, productName, region, country, fixtureSource, themeMode, postMatchTimeFrame }) || {},
      onError: this.onError.bind(this) as this['onError'],
      onPoll: this.onPoll.bind(this) as this['onPoll'],
      routingKey
    }

    this.poller = new Poller(pollerOptions)

    const isSingleFixture = fixtures?.length === 1

    this.state = {
      advertPadding: (advertType === 'persistent' && advertHeight) || 0,
      widgetData: fixtures,
      ctaOfferIsDisabledOnWidget: false,
      configOptions: {
        footerLink,
        disclaimer,
        bookmakers
      },
      isSingleFixture
    }
  }

  hideCtaOfferOnWidget() {
    this.setState({ ctaOfferIsDisabledOnWidget: true })
  }

  componentWillUnmount() {
    this.poller.stopPolling()
  }

  componentDidMount() {
    fallbackChecker.call(this)
  }

  onPoll(responseData: MainLinesData): void {
    const { fixtures, disclaimer, bookmakers, footerLink } = responseData
    const configOptions = {
      disclaimer,
      bookmakers,
      footerLink
    }
    this.setState(() => ({
      widgetData: fixtures,
      configOptions
    }))
  }

  onError(error: Error) {
    const { logger } = this.props
    const loggerError = logger.error.bind(logger) as LoggerFunction
    loggerError(error)
  }

  getFixture(widgetData: MainLinesFixtureData, configOptions: ConfigOptions, isBookmakerLinkEnabled: boolean) {
    const {
      classes,
      translations
    } = this.props
    const {
      disclaimer,
      bookmakers,
      footerLink,
    } = configOptions
    let sourceId = ''
    if (widgetData && widgetData[0] && widgetData[0].markets) {
      const availableMarket = Object.values(widgetData[0].markets).find(market => market)
      sourceId = availableMarket && availableMarket[0] && availableMarket[0].sourceId
    }
    const footerText = {
      helperInfo: {
        aboutWidgetTitle: translations.aboutWidgetTitle,
        helperInfoContent: translations.helperInfoContent,
        helperInfoDisclaimer: translations.helperInfoDisclaimer,
        helperInfoFooterText: translations.helperInfoFooterText,
        optOutText: translations.optOutText
      },
      oddsByLabel: translations.oddsByLabel,
      poweredBy: translations.poweredBy
    }

    return widgetData.map((data, index) =>
      <div className={classes.singleContainer}>
        {
          isPostMatchStatus(data.matchSummaries) ?
            <PostMatchComponent key={index} containerRef={this.containerRef} data={data} /> :
            <MainLinesComponent key={index}
              data={data}
              configOptions={configOptions}
              isBookmakerLinkEnabled={isBookmakerLinkEnabled}
              sourceId={sourceId}
              hideCtaOfferOnWidget={() => this.hideCtaOfferOnWidget()}
              ctaOfferIsDisabledOnWidget={this.state.ctaOfferIsDisabledOnWidget}
            />
        }
        {
          !isPostMatchStatus(data.matchSummaries) && <BookmakerFooter
            bookmakers={bookmakers}
            classes={{
              bookmakerContainer: classes.bookmakerContainer,
              bookmakerLogo: classes.bookmakerLogo,
              ctaLabel: classes.ctaLabel,
              oddsByBookmaker: classes.oddsByBookmaker,
              root: classes.bookmakerFooter,
              disclaimerText: classes.disclaimerText
            }}
            containerRef={this.containerRef}
            disclaimer={disclaimer}
            footerLink={footerLink}
            sourceId={sourceId}
            footerText={footerText}
            showCTA
            showHelperInfo
            isSingleFixture={this.state.isSingleFixture}
          />
        }
      </div>

    )
  }

  render() {
    const { advertPadding, widgetData, configOptions } = this.state
    const {
      classes,
      config: {
        BASE_ASSET_PATH,
        showRecords,
        isBookmakerLinkEnabled
      }
    } = this.props
    configOptions.showRecords = showRecords
    const stylesCarousel = {
      carouselButton: classes.carouselButton,
      carouselButtonLeft: classes.carouselButtonLeft,
      carouselButtonRight: classes.carouselButtonRight,
      controlCaptions: classes.controlCaptions,
      controlIconsSvg: classes.controlIconsSvg,
      navigationElements: classes.navigationElements
    }

    let sourceId = ''
    if (widgetData && widgetData[0] && widgetData[0].markets) {
      const availableMarket = Object.values(widgetData[0].markets).find(market => market)
      sourceId = availableMarket && availableMarket[0] && availableMarket[0].sourceId
    }

    return (
      <>
        {
          widgetData && widgetData.length > 0 &&
          <div className={classes.container} ref={this.containerRef} style={{ borderBottom: `${advertPadding}px solid transparent` }}>
            {widgetData.length > 1
              ? <Carousel
                classes={stylesCarousel}
                iconViewBox='0 0 8 12'
                isContinuous={false}
                onSlideRender={getOnSlideRenderHandler(this.props, this.state)}
                showIndicatorDots={false}
                svgIconLinkHrefLeft={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-left`}
                svgIconLinkHrefRight={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-right`}
              >
                {this.getFixture(widgetData, configOptions, isBookmakerLinkEnabled)}
              </Carousel>
              : this.getFixture(widgetData, configOptions, isBookmakerLinkEnabled)}
          </div>
        }
      </>
    )
  }
}

const MainLinesEnhanced = compose(
  withMuiThemeProvider,
  withHotspotTracking,
  withStyles(styles, { name: 'MainLines' }),
  withTranslations((t: TranslationFunction) => {
    return {
      oddsByLabel: t('oddsBy.translation', {
        defaultValue: 'Odds by',
        context: 'Label to point to the bookmaker odds in MainLines widget'
      }),
      aboutWidgetTitle: t('about-title.translation', {
        defaultValue: 'About Main Lines',
        context: 'Text to display as title in the info popup in MainLines widget'
      }),
      helperInfoContent: t('about-info.translation', {
        defaultValue: 'Bringing you the best in pre-match and inplay sports data, our Main Lines widget gives you the info you need to fuel your hobby',
        context: 'Text to display as content inside info popup in MainLines widget'
      }),
      helperInfoDisclaimer: t('about-odds.translation', {
        defaultValue: 'Odds are subject to change at any time.',
        context: 'Text to display inside info popup as disclaimer regarding odds update in MainLines widget'
      }),
      helperInfoFooterText: t('about-footer.translation', {
        defaultValue: 'Powered by Genius Sports',
        context: 'Text to display in the info popup footer in MainLines widget'
      }),
      poweredBy: t('poweredBy.translation', {
        defaultValue: 'Powered by Genius Sports',
        context: 'Text to display besides the Genius Sports logo to indicate authoring in MainLines widget'
      }),
      optOutText: t('optOutText.translation', {
        defaultValue: 'Hide odds',
        context: 'Text to display as label for hide odds link'
      })
    }
  })
)(MainLines) as React.Component

export default (widgetInitializationParameters: WidgetInitializationParameters) => {
  return composeWidget(MainLinesEnhanced, widgetInitializationParameters) as React.ElementType
}

createWidgetInitFunction(MainLinesEnhanced, 'MainLines')
